import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import Cookies from 'js-cookie';
import { getAppTheme } from './theme';
import { getTranslation } from './i18n';
import App from './App';

const RootComponent = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const savedTheme = Cookies.get('theme');
  const savedLang = Cookies.get('lang') || 'fa';

  const [isDarkMode, setIsDarkMode] = useState(savedTheme ? savedTheme === 'dark' : prefersDarkMode);
  const [language, setLanguage] = useState(savedLang);

  useEffect(() => {
    setIsDarkMode(savedTheme ? savedTheme === 'dark' : prefersDarkMode);
  }, [prefersDarkMode, savedTheme]);

  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDarkMode ? '#0B0F17' : '#F8FAFC');
    }
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.dir = language === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleTheme = () => {
    const newTheme = !isDarkMode ? 'dark' : 'light';
    setIsDarkMode(!isDarkMode);
    Cookies.set('theme', newTheme, { expires: 365 });
  };

  const toggleLanguage = () => {
    const newLang = language === 'fa' ? 'en' : 'fa';
    setLanguage(newLang);
    Cookies.set('lang', newLang, { expires: 365 });
  };

  const t = useCallback((key, params) => getTranslation(language, key, params), [language]);

  const theme = getAppTheme(isDarkMode ? 'dark' : 'light', language === 'fa' ? 'rtl' : 'ltr');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App
        toggleTheme={toggleTheme}
        language={language}
        toggleLanguage={toggleLanguage}
        t={t}
      />
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(<RootComponent />);

// Register PWA Service Worker for offline performance
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch((err) => {
      console.error('ServiceWorker registration failed: ', err);
    });
  });
}